<template>
    <h2 class="text-center">
        <WrGradientText :text="heading" append-class=" pb-1 lg:pb-4" />
        {{ headingSuffix }}
    </h2>
    <p class="sm:text-lg text-[var(--foreground)] tracking-[0.5px] pt-3 mx-auto text-center px-4 max-sm:p-0 lg:px-64 max-sm:mt-1">
        {{ intro }}
    </p>

    <div class="grid grid-cols-1 md:grid-cols-3 gap-[26px] mt-12 lg:mt-16">
        <div
            class="p-px bg-[linear-gradient(93.94deg,rgba(168,182,255,0.82)_1.26%,rgba(204,213,255,0.82)_96.06%)] dark:bg-[linear-gradient(135.39deg,#3840B5_7.01%,#0B0B26_93.21%)] rounded-[9px] max-w-[408px] w-full">
            <div
                class="relative z-30 overflow-hidden [background:linear-gradient(0deg,#FFFFFF,#FFFFFF),linear-gradient(0deg,#F8FAFC,#F8FAFC)] dark:bg-[linear-gradient(118.11deg,#0D0D42_4.47%,#040717_79.8%)] rounded-[9px] max-w-[408px] w-full px-4 py-[45px] after:content-[''] after:absolute after:left-28 after:-top-16 after:w-[101px] after:h-[454px] after:rotate-[315deg] after:blur-[128px] after:dark:blur-[80px] after:bg-[linear-gradient(197.89deg,rgba(122,147,255,0.42)_13.31%,rgba(122,147,255,0)_98.96%)] after:dark:bg-[linear-gradient(162.89deg,#7A93FF_13.31%,rgba(122,147,255,0)_98.96%)] after:z-[-1]">
                <div class="flex w-full items-center justify-between pb-2 border-[#6390DA6E] border-b z-30">
                    <div class="flex gap-[6px] items-center">
                        <div class="bg-[#22D3EE] rounded-full size-[8px]" />
                        <p class="text-[11px] font-medium text-[#5A5478] dark:text-[#94A3B8]">{{ streamLabel }}</p>
                    </div>
                    <div
                        class="bg-[#ECFEFF] dark:bg-[#083344B2] text-[#0E7490] dark:text-[#67E8F9] border border-[#155E7566] dark:border-[#155E7599] rounded-[4px] px-1 py-2 text-center text-[10px]">
                        {{ streamBadge }}
                    </div>
                </div>
                <div class="mt-5 grid grid-cols-3 gap-2 w-full mx-auto z-30">
                    <template v-for="card in capabilityCards">
                        <div class=" flex flex-col items-center text-center gap-1 rounded-[8px] px-5 py-4"
                            :class="card.appendClass">
                            <div :class="card.circleClass" class="rounded-full border p-2">
                                <component :is="card.icon" class="size-16px"></component>
                            </div>
                            <h2 class="font-avant font-semibold text-xs" :class="card.headingClass">
                                {{ card.heading }}
                            </h2>
                            <p class="text-[9px] font-normal" :class="card.textClass">{{ card.text }}</p>
                        </div>
                    </template>
                </div>
                <div class="flex flex-col items-start gap-2 mt-10 z-30">
                    <p class="text-xl font-normal">{{ card1Title }}</p>
                    <p class="text-[#5A5478] dark:text-[#7782FF] text-base text-[15px]">{{ card1Description }}</p>
                </div>
            </div>
        </div>
        <div
            class="p-px bg-[linear-gradient(93.94deg,rgba(168,182,255,0.82)_1.26%,rgba(204,213,255,0.82)_96.06%)] dark:bg-[linear-gradient(135.39deg,#3840B5_7.01%,#0B0B26_93.21%)] rounded-[9px] max-w-[408px] w-full">
            <div
                class="relative z-30 overflow-hidden [background:linear-gradient(0deg,#FFFFFF,#FFFFFF),linear-gradient(0deg,#F8FAFC,#F8FAFC)] dark:bg-[linear-gradient(118.11deg,#0D0D42_4.47%,#040717_79.8%)] rounded-[9px] max-w-[408px] w-full px-4 py-[28px] after:content-[''] after:absolute after:left-28 after:-top-16 after:w-[101px] after:h-[454px] after:rotate-[315deg] after:blur-[128px] after:dark:blur-[80px] after:bg-[linear-gradient(197.89deg,rgba(122,147,255,0.42)_13.31%,rgba(122,147,255,0)_98.96%)] after:dark:bg-[linear-gradient(162.89deg,#7A93FF_13.31%,rgba(122,147,255,0)_98.96%)] after:z-[-1]">
                <div
                    class="max-w-[374px] rounded-lg p-[0.59px] dark:p-[0.7px] bg-[#D9EAFF] dark:bg-[linear-gradient(135.39deg,#3840B5_7.01%,#0B0B26_93.21%)]">
                    <div
                        class="max-w-[374px] p-4 rounded-lg bg-[#FFFFFF] dark:bg-[linear-gradient(118.11deg,#0D0D42_4.47%,#040717_79.8%)] ">
                        <div class="flex items-start justify-between w-full pb-3 border-[#1E293BCC] border-b">
                            <div class="flex flex-col gap-2">
                                <div class="bg-[#3D01A9] rounded-[4px] px-2 py-1 max-w-max">
                                    <p class="text-[#DAD1FA] text-[9px]">{{ existingComponentLabel }}</p>
                                </div>
                                <h2 class="font-avant font-medium text-sm">{{ product.name }}</h2>
                                <p class="text-xs text-[11px] text-[#5A5478] dark:text-[#94A3B8] font-normal">
                                    {{ product.description }}
                                </p>
                            </div>
                            <div class="flex flex-col items-end gap-1 w-max ml-auto">
                                <p class="text-sm font-medium">{{ product.price }}</p>
                                <p class="text-[#047857] dark:text-[#34D399] text-[9px]">{{ product.status }}</p>
                            </div>
                        </div>
                        <div class="flex items-center gap-2 w-full mt-4">
                            <div
                                class="inline-flex items-stretch overflow-hidden rounded-[8px] border border-[#BDB1FF] dark:border-[#334155] bg-[#FFFFFF] dark:bg-[#092361CC] w-[78px]">
                                <button type="button"
                                    class="h-6 flex-1 flex items-center justify-center text-[12px] leading-4 font-normal text-[#1E1B4B] dark:text-white"
                                    aria-label="Decrease quantity" @click="quantity > 1 && quantity--">
                                    −
                                </button>
                                <div
                                    class="h-6 flex-1 flex items-center justify-center border-x border-[#BDB1FF] dark:border-[#334155] text-[12px] leading-4 font-normal text-[#1E1B4B] dark:text-white">
                                    {{ quantity }}
                                </div>
                                <button type="button"
                                    class="h-6 flex-1 flex items-center justify-center text-[12px] leading-4 font-normal text-[#1E1B4B] dark:text-white"
                                    aria-label="Increase quantity" @click="quantity++">
                                    +
                                </button>
                            </div>
                            <button
                                class="bg-[linear-gradient(90deg,#0053E3_0%,#6A45FF_100%)] border border-[#FFFFFF40] text-white text-xs font-medium text-center w-full rounded-[8px] py-[6px]">{{ addBagText }}</button>
                        </div>
                    </div>
                </div>
                <div class="flex items-center justify-center gap-1 mt-4 w-full">
                    <component :is="WrTick"></component>
                    <p class="text-[#6D28D9] dark:text-[#739AFF] text-[10px]">{{ nativeThemeNote }}</p>
                </div>
                <div class="flex flex-col items-start gap-2 mt-5 z-30">
                    <p class="text-xl font-normal">{{ card2Title }}</p>
                    <p class="text-[#5A5478] dark:text-[#7782FF] text-base text-[15px]">{{ card2Description }}</p>
                </div>
            </div>
        </div>
        <div
            class="p-px bg-[linear-gradient(93.94deg,rgba(168,182,255,0.82)_1.26%,rgba(204,213,255,0.82)_96.06%)] dark:bg-[linear-gradient(135.39deg,#3840B5_7.01%,#0B0B26_93.21%)] rounded-[9px] max-w-[408px] w-full">
            <div
                class="relative z-30 overflow-hidden [background:linear-gradient(0deg,#FFFFFF,#FFFFFF),linear-gradient(0deg,#F8FAFC,#F8FAFC)] dark:bg-[linear-gradient(118.11deg,#0D0D42_4.47%,#040717_79.8%)] rounded-[9px] max-w-[408px] w-full px-4 py-[22px] after:content-[''] after:absolute after:left-28 after:-top-16 after:w-[101px] after:h-[454px] after:rotate-[315deg] after:blur-[128px] after:dark:blur-[80px] after:bg-[linear-gradient(197.89deg,rgba(122,147,255,0.42)_13.31%,rgba(122,147,255,0)_98.96%)] after:dark:bg-[linear-gradient(162.89deg,#7A93FF_13.31%,rgba(122,147,255,0)_98.96%)] after:z-[-1]">

                <div
                    class="max-w-[374px] rounded-lg dark:p-[0.7px]  dark:bg-[linear-gradient(135.39deg,#3840B5_7.01%,#0B0B26_93.21%)]">
                    <div
                        class="max-w-[374px] p-4 rounded-lg bg-[#FFFFFFE5] dark:bg-[linear-gradient(118.11deg,#0D0D42_4.47%,#040717_79.8%)] border border-[#E2E8F0CC] dark:border-none">
                        <div class="flex gap-[34px] items-center w-full pb-2 dark:border-[#1E293BCC] dark:border-b">
                            <template v-for="agent in aiAgents">
                                <div class="flex items-center justify-center py-[2px] px-2 rounded-[6px] max-w-max w-full gap-1"
                                    :class="agent.appendClass">
                                    <div class="size-[6px] rounded-full" :class="agent.contentClass" />
                                    <p class="text-[10px] font-medium">
                                        {{ agent.text }}
                                    </p>
                                </div>
                            </template>
                        </div>
                        <div
                            class="dark:p-px max-w-[342px] w-full bg-[none] dark:bg-[linear-gradient(135.39deg,#3840B5_7.01%,#0B0B26_93.21%)] border-[0.44px] dark:border-transparent rounded-[8px] mt-[14px]">
                            <div
                                class="max-w-[342px] w-full rounded-[8px] bg-[#F8FAFC99] dark:bg-[linear-gradient(118.11deg,#0D0D42_4.47%,#040717_79.8%)] border border-[#E2E8F0CC] dark:border-transparent p-4">
                                <div class="flex items-center justify-center w-full gap-2">
                                    <p class="text-xs text-[11px] text-[#5A5478] dark:text-[#94A3B8]">{{ modelsLabel }}</p>
                                    <div
                                        class="bg-[#ECFEFF] dark:bg-[#083344] border border-[#155E7566] dark:border-[#155E75CC] text-xs text-[#0E7490] dark:text-[#67E8F9] font-medium px-2 py-[2px] rounded-[4px]">
                                        {{ schemaEngineLabel }}
                                    </div>
                                </div>
                                <div
                                    class="max-w-[310px] w-full mt-[14px] bg-[#FFFFFF] dark:bg-[#060F39] border-[0.44px] border-[#E2E8F0E5] dark:border-[#312E814D] rounded-[6px] px-3 py-[13px]">
                                    <div class="flex items-center gap-2">
                                        <div
                                            class="bg-[#ECFDF5CC] dark:bg-[#022C2299] border-[0.44px] border-[#6EE7B7E5] dark:border-transparent rounded-[6px] py-2 px-1 [&_path]:fill-[#059669] dark:[&_path]:fill-[#34D399]">
                                            <component :is="WrTick"></component>
                                        </div>
                                        <div class="flex flex-col items-start gap-1">
                                            <h3 class="font-medium text-xs">
                                                {{ identicalResult.heading }}
                                            </h3>
                                            <p class="text-[10px] text-[#5A5478] dark:text-[#94A3B8]">{{ identicalResult.description }}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="flex flex-col items-start gap-2 mt-4 z-30">
                    <p class="text-xl font-normal">{{ card3Title }}</p>
                    <p class="text-[#5A5478] dark:text-[#7782FF] text-base text-[15px]">{{ card3Description }}</p>
                </div>
            </div>
        </div>
    </div>
    <WrButton :link="ctaLink || undefined" @click="$emit('cta-clicked')"
        container-class="bg-[linear-gradient(95.01deg,#8B9BFF_2.72%,#4753DC_95.35%)] mx-auto mt-12 lg:mt-[48px]"
        append-class="text-base font-normal px-6 bg-[linear-gradient(95.99deg,#4753DC_3.69%,#262B7E_96.65%)] mx-auto">
        {{ ctaText }}
    </WrButton>
</template>

<script setup>
import { ref } from 'vue'
import WrRobot from './WrRobot.vue';
import WrAddItem from './WrAddItem.vue';
import WrUiText from './WrUiText.vue';
import WrTick from './WrTick.vue';

defineProps({
    heading: {
        type: String,
        default: "Explore the Powerful",
    },
    headingSuffix: {
        type: String,
        default: "Capabilities of GenicUI",
    },
    intro: {
        type: String,
        default: "Render live AI updates using your existing components without slowing down your app or breaking your design.",
    },
    ctaLink: {
        type: String,
        default: "",
    },
    ctaText: {
        type: String,
        default: "Connect Your First Component",
    },

    // Card 1 — Live MCP Stream
    streamLabel: {
        type: String,
        default: "Live MCP Stream",
    },
    streamBadge: {
        type: String,
        default: "Zero Middleware",
    },
    capabilityCards: {
        type: Array,
        default: () => [
            {
                icon: WrRobot,
                circleClass: 'bg-[#4F46E5] dark:bg-[#4F46E533] border border-[#6366F166]',
                text: 'Emits Intent',
                heading: 'AI Agent',
                headingClass: 'text-[#1E1B4B] dark:text-[#FFFFFF]',
                appendClass: 'bg-[#F5F7FF] dark:bg-[#07134B] border border-[#6366F130]',
                textClass: 'text-[#5A5478] dark:text-[#94A3B8]'
            },
            {
                icon: WrAddItem,
                circleClass: 'bg-[#06B6D4] dark:bg-[#06B6D433] border border-[#22D3EE80]',
                text: 'SSE Protocol',
                heading: 'MCP Bridge',
                headingClass: 'text-[#0E7490] dark:text-[#A5F3FC]',
                appendClass: 'bg-[#F5FDFF] dark:bg-[#08334466] border border-[#22CCEE66]',
                textClass: 'text-[#0E7490] dark:text-[#67E8F9]'
            },
            {
                icon: WrUiText,
                circleClass: 'bg-[#022C22] border border-[#10B98166]',
                text: '12ms Render',
                heading: 'Native UI',
                headingClass: 'text-[#1E1B4B] dark:text-[#FFFFFF]',
                appendClass: 'bg-[#F5FDF8] dark:bg-[#022C224D] border border-[#04785766]',
                textClass: 'text-[#047857] dark:text-[#34D399]'
            }
        ]
    },
    card1Title: {
        type: String,
        default: "Built-In MCP Standard",
    },
    card1Description: {
        type: String,
        default: "Connect AI to your UI and sync updates instantly using your own component library.",
    },

    // Card 2 — Existing Component
    existingComponentLabel: {
        type: String,
        default: "EXISTING COMPONENT",
    },
    product: {
        type: Object,
        default: () => ({
            name: "Aurora Pro Headset",
            description: "Active Noise Cancellation • Space Grey",
            price: "$249.00",
            status: "In Stock",
        }),
    },
    addBagText: {
        type: String,
        default: "Add to Bag",
    },
    nativeThemeNote: {
        type: String,
        default: "100% Native Theme • Zero Custom CSS Required",
    },
    card2Title: {
        type: String,
        default: "Zero Frontend Rewrites",
    },
    card2Description: {
        type: String,
        default: "Works with React, Vue, or Svelte. GenicUI connects AI directly to your existing design system, no rebuilds needed.",
    },

    // Card 3 — LLM Compatibility
    aiAgents: {
        type: Array,
        default: () => [
            {
                text: 'Claude 3.5',
                textClass: 'text-[#B45309] dark:text-[#FCD34D]',
                appendClass: 'bg-[#FEF3C7] dark:bg-[#F59E0B33] border border-[#F59E0B66]',
                contentClass: 'bg-[#FBBF24]'
            },
            {
                text: 'GPT-4o',
                textClass: 'text-[#047857] dark:text-[#6EE7B7]',
                appendClass: 'bg-[#D1FAE5] dark:bg-[#022C2280] border border-[#04785766]',
                contentClass: 'bg-[#34D399]'
            },
            {
                text: 'Gemini 1.5',
                textClass: 'text-[#1D4ED8] dark:text-[#93C5FD]',
                appendClass: 'bg-[#DBEAFE] dark:bg-[#17255480] border border-[#1E40AF99]',
                contentClass: 'bg-[#22D3EE]'
            }
        ]
    },
    modelsLabel: {
        type: String,
        default: "3 Models",
    },
    schemaEngineLabel: {
        type: String,
        default: "1 Schema Engine",
    },
    identicalResult: {
        type: Object,
        default: () => ({
            heading: "Identical UI Result",
            description: "Strict Type-Safe Component Tree",
        }),
    },
    card3Title: {
        type: String,
        default: "Any LLM Compatibility",
    },
    card3Description: {
        type: String,
        default: "Works with React, Works with any AI model, Claude, Codex etc. Get the exact same reliable UI every time.",
    },
});

defineEmits(["cta-clicked"]);

const quantity = ref(1)
</script>
